<script setup lang="ts">
import { Head, Link, usePage } from '@inertiajs/vue3';
import { Facebook, Instagram, Mail, MapPin, Phone } from 'lucide-vue-next';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import type { AppPageProps, SeoProps, SiteSettings } from '@/types';

const showBackToTop = ref(false);

const onScroll = () => {
    showBackToTop.value =
        document.documentElement.scrollTop > window.innerHeight * 0.5;
};

onMounted(() => window.addEventListener('scroll', onScroll));
onUnmounted(() => window.removeEventListener('scroll', onScroll));

const scrollToTop = () => {
    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
};

const page = usePage<AppPageProps>();
const settings = page.props.siteSettings as SiteSettings;
const features = computed(() => page.props.features ?? {});
const mobileMenuOpen = ref(false);

/**
 * Métadonnées construites côté serveur (App\Support\Seo) et déjà rendues dans
 * <head> par le Blade racine. Les rendre ici aussi, avec les mêmes head-key,
 * permet à Inertia de les mettre à jour à chaque navigation SPA sans jamais
 * les dupliquer.
 */
function fallbackSeo(): SeoProps {
    const origin = page.props.appUrl.replace(/\/$/, '');
    const brand = settings.business_name || page.props.name;

    return {
        title: brand,
        description: settings.meta_description || '',
        canonical: origin + page.url.split('?')[0],
        image: `${origin}/images/home.webp`,
        imageWidth: null,
        imageHeight: null,
        imageAlt: brand,
        type: 'website',
        robots: 'index, follow',
        siteName: brand,
        locale: 'fr_FR',
        article: null,
        jsonLd: {},
    };
}

const seo = computed<SeoProps>(() => page.props.seo ?? fallbackSeo());

// `<` est échappé pour qu'aucune valeur ne puisse fermer la balise <script>.
const jsonLd = computed(() =>
    JSON.stringify(seo.value.jsonLd).replace(/</g, '\\u003c'),
);

/**
 * `feature` désigne le drapeau de config/site.php qui conditionne le lien.
 * Un lien sans `feature` est toujours affiché.
 */
const allNavLinks = [
    { label: 'Accueil', href: '/' },
    { label: 'La Table', href: '/la-table' },
    { label: 'Les Chambres', href: '/les-chambres' },
    { label: 'Notre Histoire', href: '/notre-histoire' },
    { label: 'Nos Valeurs', href: '/nos-valeurs' },
    { label: 'Vivre Ici', href: '/vivre-ici' },
    { label: 'Moments', href: '/moments' },
    { label: 'Actualités', href: '/actualites', feature: 'blog' },
    { label: 'Contact', href: '/contact' },
];

const navLinks = computed(() =>
    allNavLinks.filter((link) => !link.feature || features.value[link.feature]),
);

const legalLinks = [
    { label: 'Mentions légales', href: '/mentions-legales' },
    {
        label: 'Politique de confidentialité',
        href: '/politique-de-confidentialite',
    },
    { label: 'CGU', href: '/conditions-generales-utilisation' },
    { label: 'CGV', href: '/conditions-generales-de-vente' },
    { label: 'Cookies', href: '/politique-de-cookies' },
];
</script>

<template>
    <Head>
        <title>{{ seo.title }}</title>
        <!-- Playfair Display et Raleway sont chargées depuis le <head> Blade :
             injectées ici, elles ne partaient qu'après l'hydratation Inertia. -->
        <meta
            head-key="seo:description"
            name="description"
            :content="seo.description"
        />
        <meta head-key="seo:robots" name="robots" :content="seo.robots" />
        <link head-key="seo:canonical" rel="canonical" :href="seo.canonical" />
        <meta head-key="seo:og:type" property="og:type" :content="seo.type" />
        <meta
            head-key="seo:og:url"
            property="og:url"
            :content="seo.canonical"
        />
        <meta
            head-key="seo:og:title"
            property="og:title"
            :content="seo.title"
        />
        <meta
            head-key="seo:og:description"
            property="og:description"
            :content="seo.description"
        />
        <meta
            head-key="seo:og:image"
            property="og:image"
            :content="seo.image"
        />
        <meta
            v-if="seo.imageWidth && seo.imageHeight"
            head-key="seo:og:image:width"
            property="og:image:width"
            :content="String(seo.imageWidth)"
        />
        <meta
            v-if="seo.imageWidth && seo.imageHeight"
            head-key="seo:og:image:height"
            property="og:image:height"
            :content="String(seo.imageHeight)"
        />
        <meta
            head-key="seo:og:image:alt"
            property="og:image:alt"
            :content="seo.imageAlt"
        />
        <meta
            head-key="seo:og:locale"
            property="og:locale"
            :content="seo.locale"
        />
        <meta
            head-key="seo:og:site_name"
            property="og:site_name"
            :content="seo.siteName"
        />
        <meta
            v-if="seo.article?.published"
            head-key="seo:article:published_time"
            property="article:published_time"
            :content="seo.article.published"
        />
        <meta
            v-if="seo.article?.modified"
            head-key="seo:article:modified_time"
            property="article:modified_time"
            :content="seo.article.modified"
        />
        <meta
            head-key="seo:twitter:card"
            name="twitter:card"
            content="summary_large_image"
        />
        <meta
            head-key="seo:twitter:title"
            name="twitter:title"
            :content="seo.title"
        />
        <meta
            head-key="seo:twitter:description"
            name="twitter:description"
            :content="seo.description"
        />
        <meta
            head-key="seo:twitter:image"
            name="twitter:image"
            :content="seo.image"
        />
        <meta
            head-key="seo:twitter:image:alt"
            name="twitter:image:alt"
            :content="seo.imageAlt"
        />
        <component
            :is="'script'"
            head-key="seo:jsonld"
            type="application/ld+json"
            >{{ jsonLd }}</component
        >
    </Head>

    <div
        style="
            font-family: 'Raleway', 'Helvetica Neue', sans-serif;
            display: flex;
            min-height: 100vh;
            flex-direction: column;
            background-color: var(--background);
            color: var(--foreground);
        "
    >
        <!-- Hamburger button (fixe, toujours visible) -->
        <button
            style="
                position: fixed;
                top: 28px;
                left: 28px;
                z-index: 100;
                background: none;
                border: none;
                cursor: pointer;
                padding: 10px;
                display: flex;
                flex-direction: column;
                gap: 8px;
                align-items: center;
                justify-content: center;
            "
            :aria-label="mobileMenuOpen ? 'Fermer le menu' : 'Ouvrir le menu'"
            @click="mobileMenuOpen = !mobileMenuOpen"
        >
            <span
                style="
                    display: block;
                    width: 36px;
                    height: 2px;
                    background-color: #ffffff;
                    transition:
                        transform 0.25s,
                        opacity 0.25s;
                    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.6));
                "
                :style="
                    mobileMenuOpen
                        ? 'transform:translateY(10px) rotate(45deg)'
                        : ''
                "
            />
            <span
                style="
                    display: block;
                    width: 36px;
                    height: 2px;
                    background-color: #ffffff;
                    transition: opacity 0.25s;
                    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.6));
                "
                :style="mobileMenuOpen ? 'opacity:0' : ''"
            />
            <span
                style="
                    display: block;
                    width: 36px;
                    height: 2px;
                    background-color: #ffffff;
                    transition:
                        transform 0.25s,
                        opacity 0.25s;
                    filter: drop-shadow(0 1px 3px rgba(0, 0, 0, 0.6));
                "
                :style="
                    mobileMenuOpen
                        ? 'transform:translateY(-10px) rotate(-45deg)'
                        : ''
                "
            />
        </button>

        <!-- Overlay menu -->
        <Transition name="menu-overlay">
            <div
                v-if="mobileMenuOpen"
                style="
                    position: fixed;
                    inset: 0;
                    z-index: 90;
                    background-color: rgba(15, 15, 12, 0.97);
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    gap: 0;
                    overflow-y: auto;
                    padding: 80px 20px 48px;
                    box-sizing: border-box;
                "
            >
                <div
                    style="
                        width: 40px;
                        height: 1px;
                        background-color: #c17f24;
                        margin-bottom: clamp(16px, 3vh, 40px);
                        opacity: 0.6;
                    "
                />
                <nav
                    style="
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        gap: 4px;
                    "
                >
                    <Link
                        v-for="link in navLinks"
                        :key="link.href"
                        :href="link.href"
                        style="
                            font-family: 'Playfair Display', Georgia, serif;
                            font-size: clamp(1.2rem, 3.5vw + 0.5vh, 2.2rem);
                            font-weight: 400;
                            color: #ffffff;
                            text-decoration: none;
                            padding: clamp(6px, 1.2vh, 12px) 40px;
                            letter-spacing: 0.06em;
                            transition: color 0.2s;
                            text-align: center;
                        "
                        @click="mobileMenuOpen = false"
                        @mouseenter="
                            ($event.target as HTMLElement).style.color =
                                '#C17F24'
                        "
                        @mouseleave="
                            ($event.target as HTMLElement).style.color =
                                '#ffffff'
                        "
                    >
                        {{ link.label }}
                    </Link>
                </nav>
                <div
                    style="
                        width: 40px;
                        height: 1px;
                        background-color: #c17f24;
                        margin-top: clamp(16px, 3vh, 40px);
                        opacity: 0.6;
                    "
                />
                <Link
                    href="/reservation"
                    style="
                        display: inline-block;
                        margin-top: clamp(16px, 2.5vh, 32px);
                        padding: clamp(10px, 1.5vh, 14px) 40px;
                        background-color: #c17f24;
                        color: #1a1a18;
                        font-family: 'Raleway', sans-serif;
                        font-size: 0.65rem;
                        font-weight: 700;
                        letter-spacing: 0.2em;
                        text-transform: uppercase;
                        text-decoration: none;
                        transition: background-color 0.2s;
                    "
                    @click="mobileMenuOpen = false"
                    @mouseenter="
                        ($event.target as HTMLElement).style.backgroundColor =
                            '#a86e1f'
                    "
                    @mouseleave="
                        ($event.target as HTMLElement).style.backgroundColor =
                            '#C17F24'
                    "
                >
                    Réserver
                </Link>
                <p
                    style="
                        font-size: 0.6rem;
                        letter-spacing: 0.25em;
                        color: #a09890;
                        text-transform: uppercase;
                        margin-top: 24px;
                    "
                >
                    Hôtel-restaurant · Pierrefitte-sur-Loire
                </p>
            </div>
        </Transition>

        <!-- Bouton remonter en haut -->
        <Transition name="back-top">
            <button
                v-if="showBackToTop"
                @click="scrollToTop"
                style="
                    position: fixed;
                    bottom: 32px;
                    right: 28px;
                    z-index: 200;
                    background: none;
                    border: none;
                    padding: 6px;
                    cursor: pointer;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 4px;
                    filter: drop-shadow(0 1px 4px rgba(0, 0, 0, 0.4));
                "
                aria-label="Remonter en haut"
            >
                <svg
                    width="11"
                    height="7"
                    viewBox="0 0 11 7"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M1 6L5.5 1L10 6"
                        stroke="#C17F24"
                        stroke-width="1.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
                <div
                    style="
                        width: 1px;
                        height: 16px;
                        background-color: #c17f24;
                        opacity: 0.6;
                    "
                />
            </button>
        </Transition>

        <!-- Main content -->
        <main style="flex: 1">
            <slot />
        </main>

        <!-- Footer -->
        <footer
            style="
                background-color: #374137;
                position: relative;
                scroll-snap-align: start;
                scroll-snap-stop: always;
            "
        >
            <!-- Grain texture -->
            <svg
                style="
                    position: absolute;
                    width: 0;
                    height: 0;
                    overflow: hidden;
                "
            >
                <filter
                    id="grain-footer"
                    x="0%"
                    y="0%"
                    width="100%"
                    height="100%"
                    color-interpolation-filters="sRGB"
                >
                    <feTurbulence
                        type="turbulence"
                        baseFrequency="0.04 0.06"
                        numOctaves="6"
                        seed="5"
                        stitchTiles="stitch"
                    />
                    <feColorMatrix type="saturate" values="0" />
                </filter>
            </svg>
            <div
                style="
                    position: absolute;
                    inset: 0;
                    filter: url(#grain-footer);
                    opacity: 0.07;
                    mix-blend-mode: overlay;
                    pointer-events: none;
                "
            />

            <!-- Ligne ornementale supérieure -->
            <div style="display: flex; align-items: center; position: relative">
                <div
                    style="
                        flex: 1;
                        height: 1px;
                        background: linear-gradient(
                            to right,
                            transparent,
                            rgba(193, 127, 36, 0.5)
                        );
                    "
                />
                <div
                    style="
                        width: 6px;
                        height: 6px;
                        background-color: #c17f24;
                        transform: rotate(45deg);
                        margin: 0 18px;
                        flex-shrink: 0;
                    "
                />
                <div
                    style="
                        flex: 1;
                        height: 1px;
                        background: linear-gradient(
                            to left,
                            transparent,
                            rgba(193, 127, 36, 0.5)
                        );
                    "
                />
            </div>

            <!-- Contenu horizontal -->
            <div
                style="
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 36px 40px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    gap: 40px;
                    flex-wrap: wrap;
                    position: relative;
                "
            >
                <!-- Brand -->
                <div style="flex-shrink: 0">
                    <p
                        style="
                            font-size: 0.45rem;
                            letter-spacing: 0.35em;
                            text-transform: uppercase;
                            color: #c17f24;
                            font-weight: 600;
                            margin: 0 0 6px;
                        "
                    >
                        Hôtel · Restaurant
                    </p>
                    <h2
                        style="
                            font-family: 'Playfair Display', Georgia, serif;
                            font-size: 1.5rem;
                            font-weight: 600;
                            color: #ffffff;
                            margin: 0 0 4px;
                            letter-spacing: 0.05em;
                        "
                    >
                        {{ settings.business_name || 'La Péniche' }}
                    </h2>
                    <p
                        style="
                            font-family: 'Playfair Display', Georgia, serif;
                            font-size: 0.78rem;
                            color: #c17f24;
                            font-style: italic;
                            margin: 0;
                        "
                    >
                        Refuge Gourmand
                    </p>
                </div>

                <!-- Séparateur vertical -->
                <div
                    style="
                        width: 1px;
                        height: 56px;
                        background: rgba(193, 127, 36, 0.2);
                        flex-shrink: 0;
                    "
                    class="hidden md:block"
                />

                <!-- Contact -->
                <div style="display: flex; flex-direction: column; gap: 8px">
                    <div
                        v-if="settings.address"
                        style="display: flex; align-items: flex-start; gap: 8px"
                    >
                        <MapPin
                            style="
                                width: 12px;
                                height: 12px;
                                flex-shrink: 0;
                                margin-top: 2px;
                                color: #c17f24;
                            "
                        />
                        <span
                            style="
                                font-size: 0.78rem;
                                color: rgba(255, 255, 255, 0.55);
                                line-height: 1.5;
                            "
                        >
                            {{ settings.address
                            }}<span v-if="settings.zip_code || settings.city">
                                · {{ settings.zip_code }}
                                {{ settings.city }}</span
                            >
                        </span>
                    </div>
                    <div
                        v-if="settings.phone"
                        style="display: flex; align-items: center; gap: 8px"
                    >
                        <Phone
                            style="width: 12px; height: 12px; color: #c17f24"
                        />
                        <a
                            :href="`tel:${settings.phone}`"
                            style="
                                font-size: 0.78rem;
                                color: rgba(255, 255, 255, 0.55);
                                text-decoration: none;
                                transition: color 0.2s;
                            "
                            @mouseenter="
                                ($event.target as HTMLElement).style.color =
                                    '#ffffff'
                            "
                            @mouseleave="
                                ($event.target as HTMLElement).style.color =
                                    'rgba(255,255,255,0.55)'
                            "
                            >{{ settings.phone }}</a
                        >
                    </div>
                    <div
                        v-if="settings.email"
                        style="display: flex; align-items: center; gap: 8px"
                    >
                        <Mail
                            style="width: 12px; height: 12px; color: #c17f24"
                        />
                        <a
                            :href="`mailto:${settings.email}`"
                            style="
                                font-size: 0.78rem;
                                color: rgba(255, 255, 255, 0.55);
                                text-decoration: none;
                                transition: color 0.2s;
                            "
                            @mouseenter="
                                ($event.target as HTMLElement).style.color =
                                    '#ffffff'
                            "
                            @mouseleave="
                                ($event.target as HTMLElement).style.color =
                                    'rgba(255,255,255,0.55)'
                            "
                            >{{ settings.email }}</a
                        >
                    </div>
                </div>

                <!-- Séparateur vertical -->
                <div
                    style="
                        width: 1px;
                        height: 56px;
                        background: rgba(193, 127, 36, 0.2);
                        flex-shrink: 0;
                    "
                    class="hidden md:block"
                />

                <!-- Navigation -->
                <nav
                    style="
                        display: flex;
                        gap: 24px;
                        flex-wrap: wrap;
                        align-items: center;
                    "
                >
                    <Link
                        v-for="link in navLinks"
                        :key="link.href"
                        :href="link.href"
                        style="
                            font-size: 0.75rem;
                            color: rgba(255, 255, 255, 0.55);
                            text-decoration: none;
                            transition: color 0.2s;
                            letter-spacing: 0.06em;
                            text-transform: uppercase;
                        "
                        @mouseenter="
                            ($event.target as HTMLElement).style.color =
                                '#ffffff'
                        "
                        @mouseleave="
                            ($event.target as HTMLElement).style.color =
                                'rgba(255,255,255,0.55)'
                        "
                        >{{ link.label }}</Link
                    >
                </nav>

                <!-- Séparateur vertical -->
                <div
                    style="
                        width: 1px;
                        height: 56px;
                        background: rgba(193, 127, 36, 0.2);
                        flex-shrink: 0;
                    "
                    class="hidden md:block"
                />

                <!-- Réseaux sociaux -->
                <div style="display: flex; gap: 10px; align-items: center">
                    <a
                        v-if="settings.facebook"
                        :href="settings.facebook"
                        target="_blank"
                        rel="noopener noreferrer"
                        style="
                            width: 34px;
                            height: 34px;
                            border: 1px solid rgba(193, 127, 36, 0.3);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            color: rgba(255, 255, 255, 0.55);
                            transition:
                                border-color 0.2s,
                                color 0.2s;
                        "
                        aria-label="Facebook"
                        @mouseenter="
                            (
                                $event.currentTarget as HTMLElement
                            ).style.borderColor = '#C17F24';
                            ($event.currentTarget as HTMLElement).style.color =
                                '#C17F24';
                        "
                        @mouseleave="
                            (
                                $event.currentTarget as HTMLElement
                            ).style.borderColor = 'rgba(201,169,107,0.3)';
                            ($event.currentTarget as HTMLElement).style.color =
                                'rgba(255,255,255,0.55)';
                        "
                        ><Facebook style="width: 14px; height: 14px"
                    /></a>
                    <a
                        v-if="settings.instagram"
                        :href="settings.instagram"
                        target="_blank"
                        rel="noopener noreferrer"
                        style="
                            width: 34px;
                            height: 34px;
                            border: 1px solid rgba(193, 127, 36, 0.3);
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            color: rgba(255, 255, 255, 0.55);
                            transition:
                                border-color 0.2s,
                                color 0.2s;
                        "
                        aria-label="Instagram"
                        @mouseenter="
                            (
                                $event.currentTarget as HTMLElement
                            ).style.borderColor = '#C17F24';
                            ($event.currentTarget as HTMLElement).style.color =
                                '#C17F24';
                        "
                        @mouseleave="
                            (
                                $event.currentTarget as HTMLElement
                            ).style.borderColor = 'rgba(201,169,107,0.3)';
                            ($event.currentTarget as HTMLElement).style.color =
                                'rgba(255,255,255,0.55)';
                        "
                        ><Instagram style="width: 14px; height: 14px"
                    /></a>
                </div>
            </div>

            <!-- Barre copyright -->
            <div
                style="
                    border-top: 1px solid rgba(193, 127, 36, 0.12);
                    padding: 12px 40px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    flex-wrap: wrap;
                    gap: 8px;
                    position: relative;
                "
            >
                <span
                    style="
                        font-size: 0.6rem;
                        color: rgba(255, 255, 255, 0.25);
                        letter-spacing: 0.08em;
                        text-transform: uppercase;
                    "
                >
                    &copy; {{ new Date().getFullYear() }}
                    {{ settings.business_name || 'Auberge la Péniche' }}
                    &ensp;&middot;&ensp; Tous droits réservés
                </span>
                <div style="display: flex; align-items: center; gap: 20px">
                    <nav style="display: flex; gap: 16px">
                        <Link
                            v-for="link in legalLinks"
                            :key="link.href"
                            :href="link.href"
                            style="
                                font-size: 0.6rem;
                                color: rgba(255, 255, 255, 0.25);
                                text-decoration: none;
                                transition: color 0.2s;
                                letter-spacing: 0.05em;
                            "
                            @mouseenter="
                                ($event.target as HTMLElement).style.color =
                                    'rgba(255,255,255,0.6)'
                            "
                            @mouseleave="
                                ($event.target as HTMLElement).style.color =
                                    'rgba(255,255,255,0.25)'
                            "
                            >{{ link.label }}</Link
                        >
                    </nav>
                </div>
            </div>

            <!-- Crédit — position: relative pour passer au-dessus du grain. -->
            <div
                style="
                    padding: 0 40px 14px;
                    text-align: center;
                    position: relative;
                "
            >
                <span
                    style="
                        font-size: 0.6rem;
                        color: rgba(255, 255, 255, 0.25);
                        letter-spacing: 0.08em;
                    "
                >
                    Site réalisé par
                    <a
                        href="https://arthur-giraud.fr"
                        target="_blank"
                        rel="noopener noreferrer"
                        style="
                            color: inherit;
                            text-decoration: none;
                            transition: color 0.2s;
                        "
                        @mouseenter="
                            ($event.target as HTMLElement).style.color =
                                'rgba(255,255,255,0.6)'
                        "
                        @mouseleave="
                            ($event.target as HTMLElement).style.color =
                                'inherit'
                        "
                        >Arthur Giraud</a
                    >
                </span>
            </div>
        </footer>
    </div>
</template>

<style scoped>
.menu-overlay-enter-active,
.menu-overlay-leave-active {
    transition: opacity 0.3s ease;
}
.menu-overlay-enter-from,
.menu-overlay-leave-to {
    opacity: 0;
}

.back-top-enter-active,
.back-top-leave-active {
    transition:
        opacity 0.3s ease,
        transform 0.3s ease;
}
.back-top-enter-from,
.back-top-leave-to {
    opacity: 0;
    transform: translateY(8px);
}
</style>
